/**
 * Corpse loot panel — center-right overlay listing the corpse's gold + each
 * non-empty item slot. Click a row to fire `pickup` against that slot.
 *
 * Per Phase 7 plan (D6): the pickup wire takes `{ corpseId, slot }`. Each
 * non-empty item slot becomes a row; the gold pile (if > 0) becomes a
 * "Take Gold (NN)" row at the top. After every click, refresh re-reads the
 * corpse from state and re-renders. Closes when corpse despawns or all loot
 * is taken.
 *
 * Mirrors craft-menu.ts factory shape: `{ open, close, isOpen, refresh }`.
 */
import { ITEMS, type HelloState } from "@tribez/shared";

export interface CorpseLoot {
  open(corpseId: string): void;
  close(): void;
  isOpen(): boolean;
  /** Re-render rows from current corpse state. Closes if corpse is gone or
   *  fully empty. Call each frame while open. */
  refresh(state: HelloState): void;
}

type SendFn = (type: string, payload: unknown) => void;

export function createCorpseLoot(send: SendFn): CorpseLoot {
  const root = document.createElement("div");
  root.id = "corpse-loot";
  root.style.display = "none";
  document.body.appendChild(root);

  let opened = false;
  let currentCorpseId: string | null = null;
  // Cache the last-rendered signature so we don't churn DOM unnecessarily.
  let lastSig = "";

  function close(): void {
    opened = false;
    currentCorpseId = null;
    lastSig = "";
    root.style.display = "none";
    root.innerHTML = "";
  }

  function open(corpseId: string): void {
    opened = true;
    currentCorpseId = corpseId;
    lastSig = "";
    root.style.display = "flex";
    root.innerHTML = `
      <div class="corpse-loot__hdr">
        <span>Corpse</span>
        <span class="corpse-loot__close">[Esc] close</span>
      </div>
      <div class="corpse-loot__list"></div>
    `;
    root.querySelector<HTMLSpanElement>(".corpse-loot__close")!
      .addEventListener("click", () => close());
  }

  function refresh(state: HelloState): void {
    if (!opened || !currentCorpseId) return;
    const corpse = state.corpses.get(currentCorpseId);
    if (!corpse) { close(); return; }

    // Build a signature so we only re-render when contents change.
    const slots: Array<{ key: string; itemId: string; count: number }> = [];
    corpse.items.forEach((slot, key) => {
      if (slot.itemId !== "" && slot.count > 0) {
        slots.push({ key, itemId: slot.itemId, count: slot.count });
      }
    });
    const sig = `${corpse.gold}|${slots.map((s) => `${s.key}:${s.itemId}:${s.count}`).join(",")}`;
    if (sig === lastSig) return;
    lastSig = sig;

    // If totally empty, close.
    if (corpse.gold === 0 && slots.length === 0) { close(); return; }

    const list = root.querySelector<HTMLDivElement>(".corpse-loot__list");
    if (!list) return;
    list.innerHTML = "";

    // Update header to include owner display name if present.
    const hdrSpan = root.querySelector<HTMLSpanElement>(".corpse-loot__hdr > span:first-child");
    if (hdrSpan) {
      hdrSpan.textContent = corpse.ownerName ? `Corpse (${corpse.ownerName})` : "Corpse";
    }

    if (corpse.gold > 0) {
      const row = document.createElement("div");
      row.className = "loot-row loot-row--gold";
      row.innerHTML = `<span class="loot-row__name">Take Gold</span><span class="loot-row__count">${corpse.gold}</span>`;
      row.addEventListener("click", () => {
        if (currentCorpseId) send("pickup", { corpseId: currentCorpseId, slot: "gold" });
      });
      list.appendChild(row);
    }

    for (const s of slots) {
      const def = ITEMS[s.itemId];
      const name = def?.displayName ?? s.itemId;
      const row = document.createElement("div");
      row.className = "loot-row";
      const countText = s.count > 1 ? `×${s.count}` : "";
      row.innerHTML = `<span class="loot-row__name">${name}</span><span class="loot-row__count">${countText}</span>`;
      row.addEventListener("click", () => {
        if (currentCorpseId) send("pickup", { corpseId: currentCorpseId, slot: s.key });
      });
      list.appendChild(row);
    }
  }

  function isOpen(): boolean {
    return opened;
  }

  return { open, close, isOpen, refresh };
}

export const PICKUP_RADIUS = 2.5;

/** Find the nearest corpse within `radius` of the cube. */
export function findNearbyCorpse(
  cube: { x: number; z: number },
  corpses: Iterable<{ id: string; x: number; z: number }>,
  radius: number = PICKUP_RADIUS,
): { id: string; dist: number } | null {
  let best: { id: string; dist: number } | null = null;
  for (const c of corpses) {
    const d = Math.hypot(c.x - cube.x, c.z - cube.z);
    if (d > radius) continue;
    if (!best || d < best.dist) best = { id: c.id, dist: d };
  }
  return best;
}
